/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAvatarUpload.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing avatar upload functionality.
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { uploadUserAvatar } from '../api/usersApi';
import { useAuthStore } from '../../../store/auth';

export function useAvatarUpload(
  mode: 'create' | 'edit',
  userId: string | undefined,
  avatar: string | null,
  setAvatar: (url: string | null) => void,
) {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { user: currentUser } = useAuthStore();
  const tenantSlug = currentUser?.tenant?.slug || t('users.defaultTenant');

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const MAX_SIZE = 2 * 1024 * 1024; // 2MB

  const getFullAvatarUrl = (avatarPath: string | null): string | null => {
    if (!avatarPath) return null;
    return avatarPath.startsWith('http') ? avatarPath : `${avatarPath}`;
  };

  const handleAvatarSelect = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Validate file size
      if (file.size > MAX_SIZE) {
        showError(t('users.errors.avatarTooLarge'));
        return;
      }

      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        showError(t('users.errors.avatarType'));
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setAvatar(result);
      };
      reader.readAsDataURL(file);

      setSelectedFile(file);

      // If editing existing user, upload immediately
      if (mode === 'edit' && userId) {
        setUploading(true);
        try {
          const result = await uploadUserAvatar(file, userId, tenantSlug);
          setAvatar(getFullAvatarUrl(result.avatarUrl));
          showSuccess(t('users.messages.avatarUploaded'));
        } catch (error) {
          const message =
            error?.response?.data?.message || t('users.errors.avatarUploadFailed');
          showError(message);
          setSelectedFile(null);
          setAvatar(avatar);
        } finally {
          setUploading(false);
        }
      } else {
        showSuccess(t('users.messages.avatarSelected'));
      }
    };
    input.click();
  };

  const handleAvatarRemove = () => {
    setAvatar(null);
    setSelectedFile(null);
  };

  const uploadAvatarAfterCreate = async (newUserId: string) => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const result = await uploadUserAvatar(selectedFile, newUserId, tenantSlug);
      setAvatar(getFullAvatarUrl(result.avatarUrl));
      showSuccess(t('users.messages.avatarUploaded'));
    } catch (error) {
      const message =
        error?.response?.data?.message || t('users.errors.avatarUploadAfterCreate');
      showError(message);
    } finally {
      setUploading(false);
      setSelectedFile(null);
    }
  };

  return {
    selectedFile,
    uploading,
    handleAvatarSelect,
    handleAvatarRemove,
    uploadAvatarAfterCreate,
  };
}

